import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Icon from "./Icon";
import { languages, translations } from "../data/siteData";

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    try {
      return window.localStorage.getItem("qubo-locale") || "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("qubo-locale", locale);
    } catch {
      // Local storage may be unavailable in private browsing contexts.
    }
    document.documentElement.lang = locale === "hi" ? "hi" : "en";
  }, [locale]);

  const copy = translations[locale] || translations.en;

  return (
    <LocaleContext.Provider value={{ locale, setLocale, copy }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}

export function Seo({ title, description, image = "/images/qg-logo.png" }) {
  const location = useLocation();

  useEffect(() => {
    const fullTitle = title ? `${title} | QuboGroup` : "QuboGroup | Build what’s next";
    const fullDescription =
      description ||
      "QuboGroup helps ambitious businesses move forward across software, infrastructure, commerce, and AI.";
    const canonicalUrl = `https://qubogroup.com${location.pathname}`;

    document.title = fullTitle;

    const upsertMeta = (attribute, key, content) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    upsertMeta("name", "description", fullDescription);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", fullDescription);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", `https://qubogroup.com${image}`);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", fullDescription);
    upsertMeta("name", "twitter:image", `https://qubogroup.com${image}`);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);
  }, [description, image, location.pathname, title]);

  return null;
}

export function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

const navItems = [
  { to: "/solutions", key: "solutions" },
  { to: "/companies", key: "companies" },
  { to: "/about", key: "about" },
  { to: "/insights", key: "insights" },
  { to: "/careers", key: "careers" },
];

export function Header() {
  const { locale, setLocale, copy } = useLocale();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header className="site-header">
      <div className="container header-inner">
        <Link className="brand" to="/" aria-label="QuboGroup home">
          <span className="brand-mark">
            <img src="/images/qg-logo.png" alt="" />
          </span>
          <span className="brand-copy">
            <strong>Qubo</strong>
            <span>GROUP</span>
          </span>
        </Link>

        <button
          className="mobile-menu-button"
          type="button"
          aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((open) => !open)}
        >
          <Icon name={mobileOpen ? "close" : "menu"} size={22} />
        </button>

        <div className={`header-panel ${mobileOpen ? "is-open" : ""}`}>
          <nav className="primary-nav" aria-label="Primary navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                {copy.nav[item.key]}
              </NavLink>
            ))}
          </nav>
          <div className="header-actions">
            <label className="language-select">
              <Icon name="globe" size={16} />
              <span className="visually-hidden">Language</span>
              <select value={locale} onChange={(event) => setLocale(event.target.value)}>
                {languages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.label}
                  </option>
                ))}
              </select>
              <Icon name="chevron" size={14} />
            </label>
            <Link className="button button-small button-primary" to="/contact">
              {copy.nav.contact}
              <Icon name="arrow" size={16} />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer">
      <div className="container footer-main">
        <div className="footer-intro">
          <Link className="brand brand-light" to="/">
            <span className="brand-mark">
              <img src="/images/qg-logo.png" alt="" />
            </span>
            <span className="brand-copy">
              <strong>Qubo</strong>
              <span>GROUP</span>
            </span>
          </Link>
          <p>
            One group helping ambitious businesses move forward across software,
            infrastructure, commerce, and AI.
          </p>
          <a className="footer-email" href="mailto:contact@qubogroup.com">
            contact@qubogroup.com
            <Icon name="arrow" size={16} />
          </a>
        </div>

        <div className="footer-column">
          <h3>Explore</h3>
          <Link to="/solutions">Solutions</Link>
          <Link to="/companies">Companies</Link>
          <Link to="/case-studies">Case studies</Link>
          <Link to="/insights">Insights</Link>
        </div>

        <div className="footer-column">
          <h3>Company</h3>
          <Link to="/about">About QuboGroup</Link>
          <Link to="/work-with-qubo">Work with Qubo</Link>
          <Link to="/careers">Careers</Link>
          <Link to="/contact">Contact</Link>
        </div>

        <div className="footer-column">
          <h3>Our companies</h3>
          <a href="https://qubosoft.com" target="_blank" rel="noreferrer">
            QuboSoft
          </a>
          <a href="https://quboinfra.com" target="_blank" rel="noreferrer">
            QuboInfra
          </a>
          <a href="https://qubokart.com" target="_blank" rel="noreferrer">
            QuboKart
          </a>
          <span className="footer-location">
            <Icon name="pin" size={15} />
            Noida, India
          </span>
        </div>
      </div>

      <div className="container footer-bottom">
        <span>© {year} QuboGroup. All rights reserved.</span>
        <div className="footer-legal">
          <Link to="/privacy-policy">Privacy</Link>
          <Link to="/terms-of-service">Terms</Link>
          <Link to="/cookie-policy">Cookies</Link>
        </div>
        <span className="footer-note">Software · Infra · Commerce · AI</span>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <>
      <ScrollToTop />
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

export function PageHero({
  eyebrow,
  title,
  description,
  children,
  image,
  imageAlt = "",
  accent = "blue",
  compact = false,
}) {
  return (
    <section className={`page-hero page-hero-${accent} ${compact ? "page-hero-compact" : ""}`}>
      <div className="container page-hero-grid">
        <div className="page-hero-copy">
          {eyebrow && <span className="eyebrow eyebrow-light">{eyebrow}</span>}
          <h1>{title}</h1>
          {description && <p>{description}</p>}
          {children && <div className="page-hero-actions">{children}</div>}
        </div>
        <div className="page-hero-visual">
          {image ? (
            <img src={image} alt={imageAlt} />
          ) : (
            <div className="hero-orbit" aria-hidden="true">
              <span className="orbit-line orbit-line-one" />
              <span className="orbit-line orbit-line-two" />
              <span className="orbit-core">
                <Icon name="spark" size={34} />
              </span>
              <span className="orbit-dot orbit-dot-one" />
              <span className="orbit-dot orbit-dot-two" />
              <span className="orbit-dot orbit-dot-three" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function SectionHeading({ eyebrow, title, description, align = "center", light = false }) {
  return (
    <div className={`section-heading align-${align} ${light ? "section-heading-light" : ""}`}>
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
  );
}

export function ButtonLink({ to, children, variant = "primary", size = "normal", external = false }) {
  const className = `button button-${variant} ${size === "small" ? "button-small" : ""}`;
  const isExternal = external || /^(https?:|mailto:|tel:)/i.test(to);
  const content = (
    <>
      <span>{children}</span>
      <Icon name="arrow" size={17} />
    </>
  );

  if (isExternal) {
    return (
      <a
        className={className}
        href={to}
        target={/^https?:/i.test(to) ? "_blank" : undefined}
        rel={/^https?:/i.test(to) ? "noreferrer" : undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <Link className={className} to={to}>
      {content}
    </Link>
  );
}

export function Tag({ children, color = "blue" }) {
  return <span className={`tag tag-${color}`}>{children}</span>;
}

export function IconBadge({ name, color = "blue", size = "normal" }) {
  return (
    <span className={`icon-badge icon-badge-${color} icon-badge-${size}`}>
      <Icon name={name} size={size === "large" ? 28 : 22} />
    </span>
  );
}
